import { useState, useEffect } from "react";
import {
  generateSceneDescriptions,
  generateImageForScene,
  uploadImageToS3,
  uploadToLeonardo,
  generateVideos,
  checkVideoStatus,
} from "../geminiService";
import refData from "../ref.md";

export const useStoryGeneration = () => {
  const [prompt, setPrompt] = useState("");
  const [numScenes, setNumScenes] = useState(3);
  const [selectedImage, setSelectedImage] = useState(null);
  const [mascotImage, setMascotImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scenes, setScenes] = useState([]);
  const [error, setError] = useState("");
  const [regeneratingScene, setRegeneratingScene] = useState(null);
  const [generatingVideos, setGeneratingVideos] = useState(false);
  const [videoResults, setVideoResults] = useState(null);
  const [videoStatuses, setVideoStatuses] = useState([]);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [showForm, setShowForm] = useState(true);
  const [currentStep, setCurrentStep] = useState("");
  const [generatingSceneIndex, setGeneratingSceneIndex] = useState(-1);
  const [videoGenerationStep, setVideoGenerationStep] = useState("");
  const [videoProgress, setVideoProgress] = useState({ current: 0, total: 0 });
  const [editingScene, setEditingScene] = useState(null);
  const [editPrompt, setEditPrompt] = useState("");
  const [regeneratingVideo, setRegeneratingVideo] = useState(null);
  const [editingVideoPrompt, setEditingVideoPrompt] = useState(null);
  const [editVideoPrompt, setEditVideoPrompt] = useState("");

  const hasAllImages = scenes.length > 0 && scenes.every((scene) => scene.imageUrl);

  useEffect(() => {
    try {
      const mascotData = refData?.contents?.[0]?.parts?.[0]?.inlineData;
      if (mascotData?.data) {
        setMascotImage({
          base64Data: mascotData.data,
          mimeType: mascotData.mimeType || "image/jpeg",
        });
      }
    } catch (err) {
      console.warn("Could not load mascot reference:", err);
    }
  }, []);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64Data = event.target.result.split(",")[1];
        setSelectedImage({
          base64Data,
          mimeType: file.type,
          file,
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const generateStory = async () => {
    if (!prompt.trim()) {
      setError("Please enter a story prompt");
      return;
    }

    setLoading(true);
    setError("");
    setScenes([]);
    setCurrentStep("Generating scene descriptions...");

    try {
      const sceneDescriptions = await generateSceneDescriptions(prompt, numScenes);
      const initialScenes = sceneDescriptions.map((scene) => ({
        ...scene,
        imageUrl: null,
        isGeneratingImage: false,
        error: null,
      }));
      setScenes(initialScenes);
      setCurrentStep("Generating images for each scene...");

      const characterRef = selectedImage || (prompt.toLowerCase().includes("mascot") ? mascotImage : null);
      const updatedScenes = [...initialScenes];

      for (let i = 0; i < sceneDescriptions.length; i++) {
        const scene = sceneDescriptions[i];
        setGeneratingSceneIndex(i);
        setCurrentStep(`Generating image for Scene ${scene.scene_number}...`);

        updatedScenes[i] = { ...updatedScenes[i], isGeneratingImage: true };
        setScenes([...updatedScenes]);

        try {
          const imageUrl = await generateImageForScene(scene, characterRef);
          updatedScenes[i] = {
            ...updatedScenes[i],
            imageUrl,
            isGeneratingImage: false,
            error: null,
          };
        } catch (err) {
          console.error(`Scene ${scene.scene_number} image generation failed:`, err);
          updatedScenes[i] = {
            ...updatedScenes[i],
            imageUrl: null,
            isGeneratingImage: false,
            error: err.message,
          };
        }

        setScenes([...updatedScenes]);
      }

      setCurrentStep("Story generation complete!");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setGeneratingSceneIndex(-1);
      setCurrentStep("");
    }
  };

  const regenerateSceneImage = async (sceneIndex) => {
    setRegeneratingScene(sceneIndex);
    setError("");

    try {
      const scene = scenes[sceneIndex];
      const characterRef = selectedImage || (prompt.toLowerCase().includes("mascot") ? mascotImage : null);
      const imageUrl = await generateImageForScene(scene, characterRef);

      const updatedScenes = [...scenes];
      updatedScenes[sceneIndex] = { ...scene, imageUrl, error: null };
      setScenes(updatedScenes);
    } catch (err) {
      console.error(`Scene ${sceneIndex + 1} regeneration failed:`, err);
      const updatedScenes = [...scenes];
      updatedScenes[sceneIndex] = { ...scenes[sceneIndex], error: err.message };
      setScenes(updatedScenes);
    } finally {
      setRegeneratingScene(null);
    }
  };

  const handleGenerateVideos = async () => {
    setGeneratingVideos(true);
    setError("");
    setVideoProgress({ current: 0, total: scenes.length });

    try {
      const scenesForVideo = [];
      setVideoGenerationStep("Preparing scenes for video generation...");

      for (let i = 0; i < scenes.length; i++) {
        const scene = scenes[i];
        setVideoProgress({ current: i + 1, total: scenes.length });

        if (!scene.imageUrl) {
          throw new Error(`Scene ${scene.scene_number} has no image`);
        }

        setVideoGenerationStep(`Uploading Scene ${scene.scene_number} image to cloud storage...`);
        const imageName = `scene_${scene.scene_number}_${Date.now()}`;
        const s3Url = await uploadImageToS3(scene.imageUrl, imageName);

        setVideoGenerationStep(`Preparing Scene ${scene.scene_number} for video generation...`);
        const { leonardo_id } = await uploadToLeonardo(s3Url, imageName);

        scenesForVideo.push({
          image_url: s3Url,
          scene_number: scene.scene_number,
          video_prompt: scene.video_prompt,
          leonardo_id: leonardo_id,
        });
      }

      setVideoGenerationStep("Submitting all scenes for video generation...");
      const result = await generateVideos(scenesForVideo);
      setVideoResults(result);

      setVideoGenerationStep("Videos submitted successfully! Monitoring progress...");

      if (result.job_ids && result.job_ids.length > 0) {
        startStatusChecking(result.job_ids);
      }
    } catch (err) {
      console.error("Video generation failed:", err);
      setError(err.message);
      setVideoGenerationStep("");
    } finally {
      setGeneratingVideos(false);
    }
  };

  const startStatusChecking = (jobIds) => {
    const interval = setInterval(async () => {
      try {
        setCheckingStatus(true);
        const statusResult = await checkVideoStatus(jobIds);
        setVideoStatuses(statusResult.job_statuses || []);

        const allComplete = statusResult.job_statuses?.every((status) => status.status === "COMPLETE");

        if (allComplete) {
          clearInterval(interval);
          setCheckingStatus(false);
        }
      } catch (err) {
        console.error("Status check failed:", err);
      }
    }, 10000);
  };

  const regenerateVideo = async (sceneIndex) => {
    setRegeneratingVideo(sceneIndex);
    setError("");

    try {
      const scene = scenes[sceneIndex];

      if (!scene.imageUrl) {
        throw new Error(`Scene ${scene.scene_number} has no image`);
      }

      const imageName = `scene_${scene.scene_number}_${Date.now()}`;
      const s3Url = await uploadImageToS3(scene.imageUrl, imageName);
      const { leonardo_id } = await uploadToLeonardo(s3Url, imageName);

      const scenesForVideo = [{
        image_url: scene.imageUrl,
        scene_number: scene.scene_number,
        video_prompt: scene.video_prompt,
        leonardo_id: leonardo_id,
      }];

      const result = await generateVideos(scenesForVideo);

      if (result.job_ids && result.job_ids.length > 0) {
        startStatusChecking(result.job_ids);
      }
    } catch (err) {
      console.error(`Video regeneration failed for scene ${sceneIndex + 1}:`, err);
      setError(err.message);
    } finally {
      setRegeneratingVideo(null);
    }
  };

  const resetForm = () => {
    setScenes([]);
    setVideoResults(null);
    setVideoStatuses([]);
    setShowForm(true);
    setError("");
    setVideoGenerationStep("");
    setVideoProgress({ current: 0, total: 0 });
    setEditingScene(null);
    setEditPrompt("");
    setRegeneratingVideo(null);
    setEditingVideoPrompt(null);
    setEditVideoPrompt("");
  };

  return {
    // State
    prompt, setPrompt,
    numScenes, setNumScenes,
    selectedImage,
    mascotImage,
    loading,
    scenes, setScenes,
    error,
    regeneratingScene,
    generatingVideos,
    videoResults,
    videoStatuses,
    checkingStatus,
    showForm, setShowForm,
    currentStep,
    generatingSceneIndex,
    videoGenerationStep,
    videoProgress,
    editingScene, setEditingScene,
    editPrompt, setEditPrompt,
    regeneratingVideo,
    editingVideoPrompt, setEditingVideoPrompt,
    editVideoPrompt, setEditVideoPrompt,
    hasAllImages,
    
    // Actions
    handleImageUpload,
    generateStory,
    regenerateSceneImage,
    handleGenerateVideos,
    regenerateVideo,
    resetForm,
  };
};